import { useEffect, useState } from 'react';
import type { LatLng } from '../bulk-photo-import';
import { useStreetViewBrowserKey } from './useStreetViewBrowserKey';
import { useGoogleMapsLoader } from './useGoogleMapsLoader';

/**
 * 単発解析フローの base_heading 自動検出 (docs/25 §7-2、Increment 3)。
 *
 * 外部参照実装 (ECW-W Flask版) の `getPanorama()` + `links[0].heading` パターンを移植:
 * 座標に最も近い Street View パノラマの隣接リンク方角 (=道路方向) を検出する。
 * `LocationPicker` の座標選択自体は GSI タイルのままで変更せず、道路方向 lookup のためだけに
 * 裏で Google Maps JS を使う。district の `streetView.enabled` が false ならロードしない。
 */

export type BaseHeadingState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'detected'; heading: number }
  | { status: 'not-found' }
  | { status: 'disabled' }
  | { status: 'error'; error: string };

export function useStreetViewBaseHeading(
  districtId: string | null,
  streetViewEnabled: boolean,
  location: LatLng | null,
): BaseHeadingState {
  const keyState = useStreetViewBrowserKey(streetViewEnabled ? districtId : null);
  const apiKey = keyState.status === 'ready' ? keyState.apiKey : null;
  const loaderState = useGoogleMapsLoader(apiKey);
  const [state, setState] = useState<BaseHeadingState>({ status: 'idle' });

  useEffect(() => {
    if (!streetViewEnabled) {
      setState({ status: 'disabled' });
      return;
    }
    if (!location) {
      setState({ status: 'idle' });
      return;
    }
    if (keyState.status === 'disabled') {
      setState({ status: 'disabled' });
      return;
    }
    if (keyState.status === 'error') {
      setState({ status: 'error', error: keyState.error });
      return;
    }
    if (loaderState.status === 'error') {
      setState({ status: 'error', error: loaderState.error });
      return;
    }
    if (loaderState.status !== 'ready') {
      setState({ status: 'loading' });
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });
    const svc = new google.maps.StreetViewService();
    svc.getPanorama(
      {
        location: { lat: location.latitude, lng: location.longitude },
        preference: google.maps.StreetViewPreference.NEAREST,
        source: google.maps.StreetViewSource.OUTDOOR,
        radius: 30,
      },
      (data, status) => {
        if (cancelled) return;
        if (status === google.maps.StreetViewStatus.OK && data?.links?.length) {
          const heading = data.links[0]?.heading;
          setState(
            typeof heading === 'number' ? { status: 'detected', heading } : { status: 'not-found' },
          );
        } else {
          setState({ status: 'not-found' });
        }
      },
    );
    return () => {
      cancelled = true;
    };
    // location はオブジェクト参照が呼出元で毎レンダー変わりうるため、緯度経度の実値で比較する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streetViewEnabled, location?.latitude, location?.longitude, keyState, loaderState]);

  return state;
}
